import {
  Alert,
  Button,
  Group,
  Loader,
  Modal,
  Table,
  Text,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons';
import { useEffect, useState } from 'react';
import {
  recalculateDailyWorksRequest,
  RecalculateDailyWorksResult,
} from '../../data/daily-work';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  errorNotification,
  successNotification,
} from '../../providers/mantine-notifications';
import { formatSignedHours } from '../../utils/helpers';
import { BalanceText } from '../balance-text';

interface RecalculateModalProps {
  opened: boolean;
  /** workdays to recalculate */
  ids: number[];
  onClose: () => void;
  /** called after the recalculation was applied (reload the list) */
  onApplied: () => void;
}

const SKIP_REASON = {
  paid: 'dia já pago',
  'no-scale': 'funcionário sem escala',
};

/**
 * Two steps: first it only SIMULATES and shows what would change (nothing is
 * written); the RH applies it knowing the effect on the bank of hours.
 */
export function RecalculateModal({
  opened,
  ids,
  onClose,
  onApplied,
}: RecalculateModalProps) {
  const isMobile = useIsMobile();
  const [preview, setPreview] = useState<RecalculateDailyWorksResult | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!opened) {
      setPreview(null);
      return;
    }
    setLoading(true);
    recalculateDailyWorksRequest({ ids })
      .then(setPreview)
      .catch(() => {
        errorNotification({
          title: 'Erro ao simular o recálculo.',
          message: 'tente novamente',
        });
        onClose();
      })
      .finally(() => setLoading(false));
  }, [opened]);

  async function handleApply() {
    setApplying(true);
    try {
      const result = await recalculateDailyWorksRequest({
        ids,
        dryRun: false,
      });
      successNotification({
        title: 'Horas recalculadas.',
        message: `${result.changed.length} dia(s) atualizado(s).`,
      });
      onApplied();
      onClose();
    } catch {
      errorNotification({
        title: 'Erro ao recalcular.',
        message: 'tente novamente',
      });
    } finally {
      setApplying(false);
    }
  }

  const changed = preview?.changed ?? [];

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Recalcular horas"
      size="xl"
      fullScreen={isMobile}
      closeOnClickOutside={false}
    >
      <Text size="sm" mb="md">
        Recalcula os dias selecionados a partir das batidas (entrada, almoço e
        saída) com a escala e as regras atuais, e atualiza o banco de horas.
        Confira abaixo o que muda antes de aplicar.
      </Text>

      {loading && <Loader size="sm" />}

      {preview && changed.length === 0 && (
        <Alert icon={<IconAlertCircle size={16} />} color="green">
          Nada a recalcular: os {preview.scanned} dia(s) já estão corretos.
        </Alert>
      )}

      {changed.length > 0 && (
        <>
          <Table>
            <thead>
              <tr>
                <th>Funcionário</th>
                <th>Dia</th>
                <th>Antes</th>
                <th>Depois</th>
                <th>Efeito no banco</th>
              </tr>
            </thead>
            <tbody>
              {changed.map((item) => (
                <tr key={item.id}>
                  <td>{item.username}</td>
                  <td>{item.date.split('-').reverse().join('/')}</td>
                  <td>{formatSignedHours(item.before.totalTime)}</td>
                  <td>{formatSignedHours(item.after.totalTime)}</td>
                  <td>
                    <BalanceText
                      value={item.bankDeltaSeconds}
                      formatted={formatSignedHours(item.bankDeltaSeconds)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Group position="right" mt="sm" spacing="xs">
            <Text size="sm">Efeito total no banco de horas:</Text>
            <BalanceText
              value={preview?.totalBankDeltaSeconds ?? 0}
              formatted={formatSignedHours(preview?.totalBankDeltaSeconds ?? 0)}
            />
          </Group>
        </>
      )}

      {!!preview?.skipped.length && (
        <Text size="sm" color="orange" mt="md">
          {preview.skipped.length} dia(s) não serão alterados:{' '}
          {Array.from(
            new Set(preview.skipped.map((s) => SKIP_REASON[s.reason])),
          ).join(', ')}
          .
        </Text>
      )}

      <Group position="right" mt="lg">
        <Button variant="default" onClick={onClose} disabled={applying}>
          Fechar
        </Button>
        <Button
          onClick={handleApply}
          loading={applying}
          disabled={loading || changed.length === 0}
        >
          Aplicar recálculo
        </Button>
      </Group>
    </Modal>
  );
}
